import caseEvidenceService from "@/features/case-evaluation/api/services/evidence-service";
import CaseEvidenceEditModal from "@/features/case-evaluation/components/case-evidence-edit-modal";
import type { CaseEvidence } from "@/features/case-evaluation/types/evidence";
import type { Media } from "@/features/dashboard/types/media";
import { PDFDocument } from "pdf-lib";
import { useEffect, useRef, useState } from "react";
import {
	Box,
	DefaultStylePanel,
	DefaultStylePanelContent,
	type Editor,
	FONT_SIZES,
	STROKE_SIZES,
	type TLAssetId,
	type TLShapeId,
	type TLUiStylePanelProps,
	Tldraw,
	loadSnapshot,
	toRichText,
	track,
	useEditor,
	useRelevantStyles,
} from "tldraw";
import { v4 as uuidv4 } from "uuid";
import { Button } from "../core/buttons/button";
import { useModal } from "../core/modals/use-modal";
import "./pdf-editor.css";

export interface PdfPage {
	src: string;
	bounds: Box;
	assetId: TLAssetId;
	shapeId: TLShapeId;
}
export interface Pdf {
	name: string;
	pages: PdfPage[];
	source: string | ArrayBuffer;
}

function getShapesWithText(editor: Editor, text: string) {
	if (!text || text.length === 0) return [];
	const shapes = editor.getCurrentPageShapes();
	const result: any[] = [];
	shapes.map((shape) => {
		const util = editor.getShapeUtil(shape);
		const shapeText = util.getText(shape);
		if (shapeText?.includes(text)) {
			result.push({ text: shapeText, shape });
		}
	});
	return result.sort((a, b) => a.text.localeCompare(b.text));
}

function CustomStylePanel({
	props,
	evidenceData,
	setIsCancelled,
	handleEvidence,
	isFullScreen,
	isLoading,
	pdf,
	onPdfUpdate,
	newExhibit,
}: {
	props: TLUiStylePanelProps;
	evidenceData: CaseEvidence;
	setIsCancelled: (values: boolean | undefined) => void;
	handleEvidence: (values: CaseEvidence) => void;
	isFullScreen?: boolean;
	isLoading: boolean;
	pdf: Pdf;
	onPdfUpdate: (pdf: Media) => void;
	newExhibit?: CaseEvidence;
}) {
	const styles = useRelevantStyles();
	const editor = useEditor();
	const modal = useModal();

	const handleSaveDrawing = async () => {
		const { document, session } = editor.getSnapshot();
		await caseEvidenceService.saveDrawing(evidenceData.id, Object.assign({ document, session }));
	};

	const handleSavePdf = async ({ name, source, pages }: Pdf) => {
		const pdf = await PDFDocument.load(source);

		const pdfPages = pdf.getPages();
		if (pdfPages.length !== pages.length) {
			throw new Error("PDF page count mismatch");
		}

		const pageShapeIds = new Set(pages.map((page) => page.shapeId));
		const allIds = Array.from(editor.getCurrentPageShapeIds()).filter((id) => !pageShapeIds.has(id));

		for (let i = 0; i < pages.length; i++) {
			const page = pages[i];
			const pdfPage = pdfPages[i];

			const exportedPng = await editor.toImage(allIds, {
				format: "png",
				background: false,
				bounds: page.bounds,
				padding: 0,
				scale: 1,
			});

			pdfPage.drawImage(await pdf.embedPng(await exportedPng.blob.arrayBuffer()), {
				x: 0,
				y: 0,
				width: pdfPage.getWidth(),
				height: pdfPage.getHeight(),
			});
		}
		const newPdfBytes = await pdf.save();
		const file = new File([newPdfBytes], `${name}`, {
			type: "application/pdf",
			lastModified: Date.now(),
		});
		onPdfUpdate?.(file);
	};

	const handleSave = (value: boolean) => {
		if (value === true) {
			handleSaveDrawing();
			handleSavePdf(pdf);
		}
	};

	const handleAddSubmarkingOnDrawing = (submarking?: CaseEvidence) => {
		modal.openModal({
			title: "Add Submarking",
			description: `Add Submarking Evidence of ${submarking?.exhibit}`,
			component: <CaseEvidenceEditModal data={undefined} newSubmarking={submarking} onSubmit={handleEvidence} isDrawing={true} handleSave={handleSave} />,
		});
	};

	return (
		<>
			<DefaultStylePanel {...props}>
				<DefaultStylePanelContent styles={styles} />
				<div className="p-2">
					<Button className="w-full gap-2 p-1 mt-1" variant="default" type="button" size="sm" onClick={() => handleAddSubmarkingOnDrawing(evidenceData)}>
						Add Submarking
					</Button>
					<Button
						className="w-full gap-2 p-1 mt-1"
						variant="green"
						type="button"
						size="sm"
						disabled={!newExhibit}
						loading={isLoading}
						onClick={() => {
							handleSave(true);
						}}
					>
						Save
					</Button>
					{!isFullScreen && (
						<Button
							className="w-full gap-2 p-1 mt-1"
							variant="danger"
							type="button"
							size="sm"
							onClick={() => {
								modal.openModal({
									type: "confirm",
									title: "Confirm Action",
									variant: "red",
									description: "Are you sure you want to exit draw mode?",
									onConfirm: async () => {
										setIsCancelled(true);
									},
								});
							}}
						>
							Exit Draw Mode
						</Button>
					)}
				</div>
			</DefaultStylePanel>
		</>
	);
}

export function TldrawPdfEditor({
	pdf,
	data,
	onPdfUpdate,
	setIsCancelled,
	newExhibit,
	handleEvidence,
	isFullScreen,
	isLoading,
}: {
	pdf: Pdf;
	data?: CaseEvidence;
	onPdfUpdate: (img: Media) => void;
	setIsCancelled?: (value: boolean) => void;
	newExhibit?: string;
	handleEvidence: (value: CaseEvidence) => void;
	isFullScreen?: boolean;
	isLoading: boolean;
}) {
	const [newPdf, setNewPdf] = useState<Media>();
	const editorRef = useRef<Editor | null>(null);
	const [renderEditor, setRenderEditor] = useState<Editor | null>(null);
	const editor = editorRef.current;
	if (renderEditor !== editor) {
		setRenderEditor(editor);
	}
	const textShapeIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (!editor) return;
		// if input is empty, remove any existing text shape
		if (!newExhibit?.trim()) {
			if (textShapeIdRef.current && editor.getShape(textShapeIdRef.current)) {
				editor.deleteShapes([textShapeIdRef.current]);
			}
			return;
		}
		const id = textShapeIdRef.current ?? `shape:${uuidv4()}`;
		textShapeIdRef.current = id;
		if (newExhibit) {
			// update the existing text shape
			editor.createShapes([
				{
					id: `shape:${uuidv4()}`,
					type: "text",
					x: 200,
					y: 200,
					props: {
						richText: toRichText(newExhibit),
						color: "black",
						font: "draw",
						size: "m",
						autoSize: true,
					},
				},
			]);
			editor.setCurrentTool("select");
		}
		const results = getShapesWithText(editor, newExhibit);
		console.log(results, "results");
	}, [newExhibit]);

	//pass image if the save button is clicked
	useEffect(() => {
		if (newPdf) onPdfUpdate?.(newPdf);
	}, [newPdf]);

	const [rendered, setRendered] = useState<boolean>(false);
	const [loadingState, setLoadingState] = useState<{ status: "loading" } | { status: "ready" } | { status: "error"; error: string }>({
		status: "loading",
	});

	useEffect(() => {
		let cancelled = false;

		const fetchAndLoad = async () => {
			try {
				if (!data?.id || !editor) return;

				const drawing = await caseEvidenceService.fetchEvidenceDrawings(data.id);

				if (cancelled) return;

				loadSnapshot(editor.store, {
					document: drawing.document,
				});

				editor.zoomToFit();

				setRendered(true);
				setLoadingState({ status: "ready" });
			} catch (error: any) {
				console.log(error);
				setLoadingState({ status: "error", error: error.message }); // Something went wrong
			}
		};

		if (!rendered && editor) {
			fetchAndLoad();
		}

		return () => {
			cancelled = true;
		};
	}, [editor, data?.id, rendered]);

	// console.log(loadingState, "laodingState");
	// console.log(newExhibit, "new exhibit");
	return (
		<Tldraw
			overrides={{
				tools: (_editor, tools) => {
					const {
						asset: _asset,
						note: _note,
						frame: _frame,
						diamond: _diamond,
						hexagon: _hexagon,
						oval: _oval,
						rhombus: _rhombus,
						"rhombus-2": _rhombus2,
						cloud: _cloud,
						star: _star,
						heart: _heart,
						triangle: _triangle,
						trapezoid: _trapezoid,
						octagon: _octagon,
						pentagon: _pentagon,
						"arrow-up": _arrowUp,
						"arrow-down": _arrowDown,
						"arrow-left": _arrowLeft,
						"arrow-right": _arrowRight,
						"check-box": _checkBox,
						"x-box": _xBox,
						embed: _embed,
						line: _line,
						highlight: _highlight,
						text: _text,
						...filteredTools
					} = tools;
					STROKE_SIZES.s = 0.5;
					STROKE_SIZES.m = 4;
					FONT_SIZES.s = 30;
					FONT_SIZES.m = 36;
					return filteredTools;
				},
			}}
			onMount={(editor) => {
				editorRef.current = editor;
				setRenderEditor(editor);

				// disable pen mode
				editor.sideEffects.registerBeforeChangeHandler("instance", (prev, next) => {
					if (next.isPenMode) {
						return prev;
					}
					return next;
				});

				// Constrain the camera to the bounds of the pages
				const targetBounds = pdf.pages.reduce((acc, page) => acc.union(page.bounds), pdf.pages[0].bounds.clone());

				function updateCameraBounds(isMobile: boolean) {
					editor.setCameraOptions({
						constraints: {
							bounds: targetBounds,
							padding: { x: isMobile ? 16 : 164, y: 64 },
							origin: { x: 0.5, y: 0 },
							initialZoom: "fit-x-100",
							baseZoom: "default",
							behavior: "contain",
						},
					});
					editor.setCamera(editor.getCamera(), { reset: true });
				}

				updateCameraBounds(editor.getViewportScreenBounds().width < 840);

				editor.setCurrentTool("draw");

				editor.updateInstanceState({ isToolLocked: true });
			}}
			components={{
				MainMenu: null,
				Minimap: null,
				ActionsMenu: null,
				InFrontOfTheCanvas: () => <PageOverlayScreen pdf={pdf} />,
				StylePanel: (props: React.ComponentProps<typeof CustomStylePanel>) => (
					<CustomStylePanel
						{...props}
						evidenceData={data}
						onPdfUpdate={setNewPdf}
						handleEvidence={handleEvidence}
						setIsCancelled={setIsCancelled}
						isFullScreen={isFullScreen}
						isLoading={isLoading}
						pdf={pdf}
						newExhibit={newExhibit}
					/>
				),
			}}
		/>
	);
}

const PageOverlayScreen = track(function PageOverlayScreen({ pdf }: { pdf?: Pdf }) {
	const editor = useEditor();

	const viewportPageBounds = editor.getViewportPageBounds();

	const relevantPageBounds = pdf?.pages
		.map((page) => {
			if (!viewportPageBounds.collides(page.bounds)) return null;
			const topLeft = editor.pageToViewport({ x: page.bounds.x, y: page.bounds.y });
			const bottomRight = editor.pageToViewport({ x: page.bounds.maxX, y: page.bounds.maxY });
			return new Box(topLeft.x, topLeft.y, bottomRight.x - topLeft.x, bottomRight.y - topLeft.y);
		})
		.filter((bounds): bounds is Box => bounds !== null);

	return (
		<>
			{relevantPageBounds?.map((bounds, i) => (
				<div
					key={i}
					className="PageOverlayScreen-outline"
					style={{
						width: bounds.w,
						height: bounds.h,
						transform: `translate(${bounds.x}px, ${bounds.y}px)`,
					}}
				/>
			))}
		</>
	);
});
